import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { appLogger, ErrorCategory } from './errorLogger';

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  skipErrorLogging?: boolean;
}

export const setupAxiosErrorInterceptor = (): () => void => {
  const interceptorId = axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      const config = error.config as ExtendedAxiosRequestConfig;
      const response = error.response;
      const message = error.message;
      const statusCode = response?.status;
      
      // Skip logging for expected 400-level errors
      const skip400Errors = config?.skipErrorLogging || false;
      if (skip400Errors && statusCode && statusCode >= 400 && statusCode < 500) {
        return Promise.reject(error);
      }
      
      // Determine category
      let category;
      if (statusCode && statusCode >= 500) {
        category = ErrorCategory.SERVER;
      } else if (statusCode === 401 || statusCode === 403) {
        category = ErrorCategory.AUTH;
      } else if (!navigator.onLine) {
        category = ErrorCategory.NETWORK;
      } else {
        category = ErrorCategory.CLIENT;
      }
      
      appLogger.error(message || 'API Request Failed', {
        metadata: {
          url: config?.url,
          method: config?.method,
          status: statusCode,
          statusText: response?.statusText,
          data: response?.data,
          requestData: config?.data
        },
        source: 'axios-interceptor',
        statusCode,
        category,
        path: config?.url || window.location.pathname,
        method: config?.method?.toUpperCase(),
        requestPayload: config?.data
      });
      
      return Promise.reject(error);
    }
  );
  
  return () => axios.interceptors.response.eject(interceptorId);
};

export default setupAxiosErrorInterceptor;
